import { newImage } from "../img";
import Img from "../img";
import { useRef, useEffect, useState, useCallback } from "react";
import shared from "../../data/js/shared";
import { Share } from '@capacitor/share';
import Head from "../head";
import Tooltip from "../tooltip";
import Ad from "../ad";
import { IS_MOBILE } from "../../data/js/server-constants";
import { isVehicle } from "../utils";
import { useShippingEstimate, usePreloadSimilarListings } from "../product-detail-hooks";
import {
  $$,
  getCanonical,
} from "../../utils/dom";
import { useUser, useUI, useProduct, useChat, useListing } from "../../contexts";
import { useSearch } from '@/contexts/SearchProvider';
import { useRouterContext } from "../../contexts/RouterProvider";
import { useIsClipped } from "../../hooks/useIsClipped";
import Showcase from "../showcase";
import SimilarListings from "../similar-listings";
import AffinityShowcase from "../affinity-showcase";
import { ReportSubMenu, ShareSubMenu } from "../submenu";
import OwnerSubMenu from "../owner-submenu";
import SwiperImages from "../product-swiper-images";
import dynamic from "next/dynamic";
import { MapPin } from "lucide-react";
import { isOverPriceThreshold as checkOverPriceThreshold, isNonShippable as checkNonShippable } from '@/utils/listing-buyability';
import { AdFramework } from "../ad-framework";
import { TrackListingView } from "../analytics/track-listing-view";

import {
  CategoryBreadcrumb,
  NewLineToBR,
  VehicleInfo,
  Condition,
  Dimensions,
  moreDescriptionButton,
  SellerInfo,
} from "./ProductDetailHelpers";
import { MobilePriceSummary, DesktopOrderInfo, buyBoxStripeFee } from "./ProductDetailPricing";
import { useProductDetailState } from "./useProductDetailState";
import { VariantPicker } from "./VariantPicker";
import DealCallout from "./DealCallout";
import { useDealCallout } from "./useDealCallout";
import { useSellerFeeStatus } from "../../hooks/useSellerFeeStatus";

const {
  moreLabel,
  otherLabel,
  moreLabel1,
  shareLabel,
  watchTooltip,
  chatTooltip,
  flagTooltip,
  shareTooltip,
  manageTooltip,
} = shared.product;

const { ariaLabel: ownerMenuAriaLabel } = shared.ownerMenu;

/**
 * The fewest other listings that make "Other items from {seller}" worth a row.
 * Fewer, and the slot shows an affinity row instead (components/affinity-showcase).
 */
const SELLER_ROW_MIN = 4;

// data/js/shared defines both.
const { appTitle, appDescription } = shared as unknown as Record<string, string>;

const HeartButton = dynamic(
  () => {
    return import("../heart-button");
  },
  { ssr: false },
);

const ProductDetail = () => {
  // Use individual context hooks
  const { watchedListings, loggedIn, profile, watchListing, unwatchListing, getWatches, isLoading: userLoading } = useUser();
  const {
    chatOpen, reportMenuOpen, messageMenuOpen, shareMenuOpen, loginOpen, productOpen,
    setChatOpen, setReportMenuOpen,
    setShareMenuOpen, setPopupContents, setPopupOpen, setLoginOpen
  } = useUI();
  const {
    productDetail, currentList, recommendedList, otherList,
    setProduct,
    setSimilarListings, similarList, currentListGroup,
    selectedVariant,
  } = useProduct();
  useSearch();
  useListing();
  const { allChats, getAllChats } = useChat();
  const { push } = useRouterContext();

  // Refs for submenu triggers
  const shareButtonRef = useRef<HTMLDivElement>(null);
  const reportButtonRef = useRef<HTMLDivElement>(null);
  // The owner menu can be opened from two places — the header ellipsis and the
  // Manage Listing button in the price panel — and it positions itself under
  // whichever one was used, so remember which that was.
  const manageButtonRef = useRef<HTMLButtonElement>(null);
  const [ownerMenuFromManage, setOwnerMenuFromManage] = useState(false);
  /**
   * The listing whose seller row came up empty even after its stand-in. Keyed
   * by listing so flipping to the next product starts fresh. While it matches,
   * the ad under that row is dropped: with no row between them, the two ad
   * frames stacked straight on top of each other.
   */
  const [emptyBelowFor, setEmptyBelowFor] = useState<number | null>(null);

  // Create state object for backward compatibility
  const state = {
    watchedListings, loggedIn, profile, chatOpen, reportMenuOpen, messageMenuOpen,
    shareMenuOpen, productDetail, loginOpen, productOpen, currentList, recommendedList, otherList
  };

  const bigPhotoSwiperRef = useRef(null);
  const thumbSwiperRef = useRef(null);

  const watchDom = useRef(null);
  const [requesting, _setRequesting] = useState(false);
  const product = state.productDetail;
  const shownListingId = Number(product?.id);
  const markBelowEmpty = useCallback(() => setEmptyBelowFor(shownListingId), [shownListingId]);

  // Extract all derived state and actions
  const {
    id, title, text, price, slashedPrice, percentOff,
    height, width, depth, weight, itemCondition, vin, mileage,
    user, photos, categoryGroupId, dataSourceId, remote_url, tag,
    slug, productDataSourceId, isGCCorMyEV,
    isOwnListing, isPartnerListing, shouldHideChatButton, ownListingChatUrl,
    isAvailable, isPendingOrSold, isBuyable,
    currentPrice, priceInfo, checkoutUrl, userName, DESC_LENGTH,
    clickEmailButton, contactShelterButton, openRemoteUrlButton,
    shorter, renderCarrierLogo,
  } = useProductDetailState({
    product: product as unknown as Record<string, unknown> | null,
    profile: profile as unknown as Record<string, unknown> | null,
    allChats: allChats as unknown as Record<string, unknown>[],
    loadChats: getAllChats,
    setPopupContents,
    setPopupOpen,
  }) as unknown as ReturnType<typeof useProductDetailState> & {
    id: string;
    title: string;
    slashedPrice: string;
    percentOff: number;
    height: number;
    width: number;
    depth: number;
    weight: number;
    itemCondition: number | null;
    vin: string;
    mileage: number;
    user: { id: string; trustLevel: number; [key: string]: unknown };
    photos: { url?: string; thumbUrl?: string; "160x160Url"?: string }[];
    tag: string;
  };

  const isOverPriceThreshold = checkOverPriceThreshold(product?.price);
  const isNonShippable = checkNonShippable(
    product as unknown as Parameters<typeof checkNonShippable>[0],
  );

  // The owner's own fee rate, for the buy box's You Earn row. Only fetched on
  // their own listing; a buyer never sees the row.
  const { feeStatus: ownerFeeStatus } = useSellerFeeStatus(Boolean(isOwnListing));

  // The Deal Score callout A/B test: only on something this buyer can buy.
  const dealCallout = useDealCallout(
    product as unknown as Parameters<typeof useDealCallout>[0],
    isBuyable && !isOwnListing && !isOverPriceThreshold,
  );

  // Hooks for fetching data (extracted to satisfy component-no-api-calls)
  const { shippingEstimate, shippingCarrier, loadingShipping, isOutOfStock, shippingArrangedSeparately, taxEstimate, taxLabel, taxRatePercent, taxApproximate } = useShippingEstimate({
    product: product as unknown as Record<string, unknown> | null,
    isGCCorMyEV,
    isPartnerListing,
    user,
    loggedIn,
    dataSourceId,
    selectedVariantVid: selectedVariant?.vid,
  });

  /**
   * The Processing line of the buy box, on the same sum checkout and the charge
   * use: the item plus the shipping it is quoted with (utils/checkout-decisions,
   * computeCheckoutCharge). It used to be the fee on the listing price alone, so
   * the buy box's Processing and Total came in under checkout's by 2.9% of the
   * shipping, on every shippable listing. No shipping term where the buy box
   * adds none: a non-shippable item, a vehicle, or shipping arranged separately.
   */
  const shippingInTotal = !isNonShippable && !isVehicle(product) && !shippingArrangedSeparately;
  const buyBoxFee = buyBoxStripeFee(currentPrice, shippingInTotal ? shippingEstimate : 0);

  const { preloadSimilarListings } = usePreloadSimilarListings({
    id: id as string,
    setSimilarListings,
    currentListGroup,
    similarList,
  });

  const description = text as string;

  /**
   * Whether the description on screen is a truncated one.
   *
   * `shorter()` cuts at DESC_LENGTH characters, so anything longer than that is
   * showing the reader an incomplete sentence and needs a way to the rest of it.
   *
   * The box is also a fixed height on desktop, so a short description with
   * blank lines between its paragraphs is cut off by the box with no character
   * cut at all. That one had no "Read More" and no click (2026-10-11), so the
   * body is measured as well.
   */
  const descriptionBodyRef = useRef<HTMLDivElement>(null);
  const bodyClipped = useIsClipped(descriptionBodyRef, description);
  const isTruncated = description.length > DESC_LENGTH || bodyClipped;

  /**
   * Open the full description in the popup.
   *
   * Shared by the "Read More" link and by the description body itself. The body
   * has looked clickable since the stylesheet gave it `cursor: pointer` and a
   * hover tint, but nothing was ever wired to it — so the affordance pointed at
   * a target that did not exist, and readers who took it up got nothing.
   */
  const openFullDescription = () => {
    if (!isTruncated) return;
    moreDescriptionButton({
      setPopupContents,
      setPopupOpen,
      product: product as unknown as Record<string, unknown>,
    });
  };

  const watchedArr = state.watchedListings?.filter(
    (listing) => listing?.id == product?.id,
  );
  const watched = watchedArr?.length === 1;

  const img = photos[0] ? newImage(photos[0].url, 640, dataSourceId) : "/images/logo.png";
  const favicon = photos[0] ? newImage(photos[0].url, 80, dataSourceId) : "/images/favicons/favicon.ico";

  useEffect(() => {
    // Fetch wishlist data if logged in and wishlist is empty
    if (loggedIn && watchedListings.length === 0) {
      void getWatches();
    }
  }, [loggedIn, product?.id, getWatches, watchedListings.length])

  return (
    <>
      <Head
        title={title || appTitle}
        description={description || appDescription}
        canonicalURL={getCanonical(id, title)}
        metaImage={img}
        metaImageAlt={title}
        favicon={favicon}
      />
      <TrackListingView
        listing={{
          id: Number(id),
          title,
          price: Number(price),
          categoryId: Number(product?.categoryId),
          categoryGroupId: Number(categoryGroupId),
          tags: product?.tags,
        }}
        isOwnListing={Boolean(isOwnListing)}
        sessionReady={!userLoading}
      />
      <div className="product" data-testid="product-page">
        <div className="category-breadcrumbs" data-testid="product-category">
          <CategoryBreadcrumb
            categoryId={product?.categoryId}
            categoryGroupId={product?.categoryGroupId}
          />
        </div>
        <section className="product-top">
          <div className="product-images-piece" data-testid="product-images-gallery">
            <SwiperImages
              images={photos}
              bigPhotoSwiperRef={bigPhotoSwiperRef}
              thumbSwiperRef={thumbSwiperRef}
              product={product}
              percentOff={percentOff}
              tag={tag}
            />
          </div>
          <h1 className="product-name" data-testid="product-title">{title}</h1>
          <div className="user-info" data-testid="product-seller-info">
            {/* "Trusted Seller" is a CSS tip on the check itself (VerifyBadge). */}
            <SellerInfo user={user} slug={slug as string} />
            <div className="action-icons">
              <Tooltip
                position="above"
                triggerElement="#share-tooltip-cont"
                theme="dark"
                triggerEvent="hover"
              >
                <span>{shareTooltip}</span>
              </Tooltip>
              <Tooltip
                position="above"
                triggerElement="#flag-tooltip-cont"
                theme="dark"
                triggerEvent="hover"
              >
                <span>{isOwnListing ? manageTooltip : flagTooltip}</span>
              </Tooltip>
              <div
                ref={shareButtonRef}
                onClick={(_e: React.MouseEvent) => {
                  if (IS_MOBILE) {
                    void Share.share({
                      url: ($$(".social-canonical")[1] as Record<string, string>)?.content || ''
                    });
                  }
                  else {
                    setShareMenuOpen(true);
                  }
                  setReportMenuOpen(false);
                }}
                id="share-tooltip-cont"
                className="action-button share-button"
                aria-label={shareLabel}
                data-testid="product-share-btn"
              >
                <svg
                  width="16"
                  height="18"
                  viewBox="0 0 16 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12.9524 5.99998C11.2716 5.99998 9.90478 4.6545 9.90478 2.99999C9.90478 1.34548 11.2716 0 12.9524 0C14.6332 0 16 1.34548 16 2.99999C16 4.6545 14.6332 5.99998 12.9524 5.99998ZM12.9524 1.49993C12.112 1.49993 11.4285 2.17269 11.4285 2.99999C11.4285 3.82729 12.112 4.50005 12.9524 4.50005C13.7928 4.50005 14.4763 3.82729 14.4763 2.99999C14.4763 2.17269 13.7928 1.49993 12.9524 1.49993ZM3.04761 11.9998C1.36684 11.9998 0 10.6543 0 8.99984C0 7.34533 1.36684 5.99985 3.04761 5.99985C4.72838 5.99985 6.09523 7.34533 6.09523 8.99984C6.09523 10.6543 4.72838 11.9998 3.04761 11.9998ZM3.04761 7.49978C2.20718 7.49978 1.52374 8.17254 1.52374 8.99984C1.52374 9.82714 2.20718 10.4999 3.04761 10.4999C3.88805 10.4999 4.57148 9.82714 4.57148 8.99984C4.57148 8.17254 3.88805 7.49978 3.04761 7.49978ZM12.9524 18C11.2716 18 9.90478 16.6545 9.90478 15C9.90478 13.3455 11.2716 12 12.9524 12C14.6332 12 16 13.3455 16 15C16 16.6545 14.6332 18 12.9524 18ZM12.9524 13.5C12.112 13.5 11.4285 14.1727 11.4285 15C11.4285 15.8273 12.112 16.5001 12.9524 16.5001C13.7928 16.5001 14.4763 15.8273 14.4763 15C14.4763 14.1727 13.7928 13.5 12.9524 13.5ZM4.99507 8.57079C4.73907 8.57079 4.4892 8.4441 4.3452 8.21384C4.12428 7.86139 4.23627 7.39933 4.59507 7.18262L8.50434 4.81483C8.8616 4.59736 9.33178 4.70684 9.55193 5.0608C9.77209 5.41325 9.66086 5.8753 9.30282 6.09201L5.39355 8.45981C5.26931 8.53477 5.13066 8.5708 4.99508 8.5708L4.99507 8.57079ZM10.4756 14.2499C10.3393 14.2499 10.2022 14.2138 10.0772 14.1389L5.1246 11.1389C4.76581 10.9222 4.65446 10.4601 4.87473 10.1069C5.09488 9.75446 5.56505 9.64411 5.92232 9.86169L10.8749 12.8617C11.2329 13.0784 11.3443 13.5405 11.124 13.8936C10.9815 14.1232 10.7316 14.2499 10.4756 14.2499H10.4756Z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <div
                ref={reportButtonRef}
                onClick={(_e: React.MouseEvent) => {
                  if (!profile?.id) {
                    setLoginOpen(true);
                    return;
                  }
                  setOwnerMenuFromManage(false);
                  setReportMenuOpen(true);
                  setShareMenuOpen(false);
                }}
                className="action-button flag-button"
                id="flag-tooltip-cont"
                aria-label={isOwnListing ? ownerMenuAriaLabel : flagTooltip}
                data-testid="product-ellipsis-btn"
              >
                <svg
                  width="8"
                  height="20"
                  viewBox="0 0 8 29"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 14.5C8 16.5021 6.37711 18.125 4.375 18.125C2.37289 18.125 0.75 16.5021 0.75 14.5C0.75 12.4979 2.37289 10.875 4.375 10.875C6.37711 10.875 8 12.4979 8 14.5Z"
                    fill="currentColor"
                  />
                  <path
                    d="M8 3.625C8 5.62711 6.37711 7.25 4.375 7.25C2.37289 7.25 0.75 5.62711 0.75 3.625C0.75 1.62289 2.37289 0 4.375 0C6.37711 0 8 1.62289 8 3.625Z"
                    fill="currentColor"
                  />
                  <path
                    d="M8 25.375C8 27.3771 6.37711 29 4.375 29C2.37289 29 0.75 27.3771 0.75 25.375C0.75 23.3729 2.37289 21.75 4.375 21.75C6.37711 21.75 8 23.3729 8 25.375Z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              {/* A seller gets listing controls here, not Report. Sellers were
                  filing abuse reports against their own listings because Report
                  was the only thing this menu offered them. Everyone else's
                  menu is unchanged. */}
              {isOwnListing ? (
                <OwnerSubMenu triggerRef={ownerMenuFromManage ? manageButtonRef : reportButtonRef} />
              ) : (
                <ReportSubMenu id={product?.id} triggerRef={reportButtonRef} />
              )}
              <ShareSubMenu triggerRef={shareButtonRef} />
            </div>
          </div>
          <div className="price-line">
            <div className="product-price-group">
              <div className="product-price" data-testid="product-price">
                <a
                  href={(product as unknown as Record<string, unknown>)?.remote_url as string}
                  target="_blank"
                  rel="nofollow noreferer"
                >
                  {slashedPrice ? (
                    <div className="flex items-center gap-3">
                      <span className="line-through text-gray-500 font-medium opacity-80 text-[22px]">
                        {slashedPrice}
                      </span>
                      <span className="font-bold text-[32px]">
                        {priceInfo}
                      </span>
                    </div>
                  ) : (
                    priceInfo
                  )}
                </a>
              </div>
            </div>
            {(product?.locationText && product?.locationText.length > 3 &&
            !product?.locationText.includes("undefined")) || (product?.user as { locationText?: string })?.locationText ? (
              <div className="location-text" data-testid="product-location">
                <MapPin className="location-icon" aria-hidden="true" />
                {product?.locationText && product?.locationText.length > 3 && !product?.locationText.includes("undefined")
                  ? product.locationText
                  : (product?.user as { locationText?: string })?.locationText}
              </div>
            ) : (
              ""
            )}

            {/*
              The tips are children of their buttons, centred on them in CSS
              (.button-tip in product.scss). The shared Tooltip placed itself
              from offsetLeft maths and pointed between the two buttons.
            */}
            <div className={`price-buttons`}>
              <div
                ref={watchDom}
                className={`watch-button ${watched ? "watched" : ""}`}
                onClick={(_e: React.MouseEvent) => {
                  if (!profile?.id) {
                    setLoginOpen(true);
                    return;
                  }
                  if (watched) {
                    void unwatchListing(product?.id);
                  } else {
                    void watchListing(product as unknown as Parameters<typeof watchListing>[0]);
                  }
                }}
                id="watch-tooltip-cont"
                data-testid="product-wishlist-btn"
              >
                <HeartButton watched={watched} listingId={product?.id} />
                <span className="button-tip" role="tooltip">{watchTooltip}</span>
              </div>
              {!shouldHideChatButton && (
                <div
                  className="chat-button"
                  id="chat-tooltip-cont"
                  data-testid="product-chat-btn"
                  onClick={(_e: React.MouseEvent) => {
                    // No GCC/MyEV or partner branch here: useProductDetailState
                    // hides this button for those listings (shouldHideChatButton);
                    // their contact buttons live in the pricing box.
                    if (!profile?.id) {
                      setLoginOpen(true);
                      return;
                    }
                    // Your own listing: the chat window cannot hold a
                    // seller's threads (see useProductDetailState), so go
                    // to them in My Chats.
                    if (ownListingChatUrl) {
                      push(ownListingChatUrl);
                      return;
                    }
                    void setChatOpen(!state.chatOpen);
                  }}
                >
                  <Img src="/images/svg/chat-white.svg" />
                  <span className="button-tip" role="tooltip">{chatTooltip}</span>
                </div>
              )}
            </div>
          </div>
          <div className={`product-description-cont ${isTruncated ? "clickable" : ""}`}>
            {/* CJ variant picker — self-gates on productDetail.variants, no-op otherwise */}
            <VariantPicker />
            {/*
              The body is a second, larger click target for the same popup the
              "Read More" link opens. The link stays because it is the keyboard
              and screen-reader route and the only visible instruction; this is
              the mouse shortcut the `cursor: pointer` on the container has been
              advertising all along.
            */}
            {/*
              No role or key handler on purpose: this is a redundant pointer
              target, and the `.show-more` anchor below is the real control with
              the accessible name and the keyboard route. Giving this div a role
              would announce the entire description as a single button.
            */}
            <div
              className="product-description"
              data-testid="product-description"
              onClick={openFullDescription}
            >
              <Condition itemCondition={itemCondition} />
              {isVehicle(product) ? <VehicleInfo vin={vin} mileage={mileage} /> : <Dimensions
                height={height}
                width={width}
                depth={depth}
                weight={weight}
              />}
              {/*
                The truncated text is the only part allowed to be clipped. The
                description sits in a fixed 280px grid row with `overflow:
                hidden`, so before this wrapper existed the "Read More" link was
                simply cut off the bottom whenever the lines above it — a
                condition line, a dimensions line and 400 characters — ran past
                the box. The link was rendered and unreachable.
              */}
              <div className="product-description__body" ref={descriptionBodyRef}>
                <NewLineToBR text={shorter(description)} />
              </div>
              {isTruncated ? (
                <a
                  href="#"
                  className="show-more"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    // The body's handler would otherwise fire for the same
                    // click, opening the popup twice and double-counting the
                    // `more_description` event.
                    e.stopPropagation();
                    openFullDescription();
                  }}
                >
                  {moreLabel}
                </a>
              ) : (
                ""
              )}
            </div>
            {/* Mobile-only price summary above Buyer Protection */}
            <MobilePriceSummary
              isBuyable={isBuyable}
              isOwnListing={isOwnListing}
              isNonShippable={isNonShippable}
              dataSourceId={dataSourceId}
              priceInfo={priceInfo}
              currentPrice={currentPrice}
              stripeFee={buyBoxFee}
              shippingEstimate={shippingEstimate}
              shippingCarrier={shippingCarrier}
              loadingShipping={loadingShipping}
              isOutOfStock={isOutOfStock}
              shippingArrangedSeparately={shippingArrangedSeparately}
              taxEstimate={taxEstimate}
              taxLabel={taxLabel}
              taxRatePercent={taxRatePercent}
              taxApproximate={taxApproximate}
              renderCarrierLogo={renderCarrierLogo}
              product={product as unknown as Record<string, unknown>}
              isOverPriceThreshold={isOverPriceThreshold}
              slashedPrice={slashedPrice}
              percentOff={percentOff}
              ownerFeeStatus={ownerFeeStatus}
            />
            <DealCallout arm={dealCallout.arm} callout={dealCallout.callout} placement="inline" />
          </div>

          {!IS_MOBILE && <div className="product-detail-ad">
            <AdFramework
              bidstream={{
                mobile: (
                  <div
                    data-ad-tagid="wgw8vFZoalasLTuQSL4Q"
                    data-zid="1018"
                    data-name="SW - sellwild.com mobile 300x250"
                  />
                ),
                desktop: (
                  <div
                    data-ad-tagid="R7wZB7IZrKPO5EJAZTjq"
                    data-zid="1015"
                    data-name="SW - sellwild.com desktop 300x250"
                  />
                ),
              }}
              adsense={
                <Ad
                  key={id}
                  className="adsbygoogle"
                  style={{
                    display: "block",
                    margin: "auto",
                    width: 300,
                    height: 250,
                  }}
                  data-ad-client="ca-pub-3154552656014103"
                  data-ad-slot="9551701632"
                />
              }
            />
          </div>}

          <DesktopOrderInfo
            product={product as unknown as Record<string, unknown>}
            isOwnListing={isOwnListing}
            isAvailable={isAvailable}
            isPendingOrSold={isPendingOrSold}
            isBuyable={isBuyable}
            isNonShippable={isNonShippable}
            isGCCorMyEV={isGCCorMyEV}
            isPartnerListing={isPartnerListing}
            dataSourceId={dataSourceId}
            remote_url={remote_url}
            priceInfo={priceInfo}
            currentPrice={currentPrice}
            stripeFee={buyBoxFee}
            slashedPrice={slashedPrice}
            percentOff={percentOff}
            shippingEstimate={shippingEstimate}
            shippingCarrier={shippingCarrier}
            loadingShipping={loadingShipping}
            isOutOfStock={isOutOfStock}
            shippingArrangedSeparately={shippingArrangedSeparately}
            taxEstimate={taxEstimate}
            taxLabel={taxLabel}
            taxRatePercent={taxRatePercent}
            taxApproximate={taxApproximate}
            renderCarrierLogo={renderCarrierLogo}
            state={state as Record<string, unknown>}
            requesting={requesting}
            setChatOpen={setChatOpen}
            clickEmailButton={clickEmailButton as () => void}
            contactShelterButton={contactShelterButton as () => void}
            openRemoteUrlButton={openRemoteUrlButton}
            checkoutUrl={checkoutUrl}
            preloadSimilarListings={preloadSimilarListings}
            productDataSourceId={productDataSourceId}
            manageButtonRef={manageButtonRef}
            ownerFeeStatus={ownerFeeStatus}
            onManageClick={() => {
              setOwnerMenuFromManage(true);
              setReportMenuOpen(true);
              setShareMenuOpen(false);
            }}
          />
          {/* Row 5 of the buy-box column, which the flip buttons left empty. */}
          <DealCallout arm={dealCallout.arm} callout={dealCallout.callout} placement="slot" />
          {/* The previous/next flip buttons are off (owner, 2026-10-09: "take the
              flip buttons out of the ui until we can figure something better").
              On a phone they sat on either side of Buy Now. FlipButtons and
              prevProduct/nextProduct are kept for whatever replaces them. */}
        </section>
        <section className="more-from-seller">
          <div className="recommended-listings">
            <SimilarListings
              title={moreLabel1}
              listingId={Number(id)}
              setProduct={setProduct as (product: unknown) => void}
              tag={tag}
              lazy={true}
            />
          </div>
          {!IS_MOBILE && <div className="recommended-ad">
            <AdFramework
              bidstream={{
                mobile: (
                  <div
                    data-ad-tagid="MgQGXBYSCw7tyxIwr83m"
                    data-zid="1020"
                    data-name="SW - sellwild.com mobile 320x50"
                  />
                ),
                desktop: (
                  <div
                    data-ad-tagid="Yp4BKeY0meZZk0eOYBue"
                    data-zid="1014"
                    data-name="SW - sellwild.com desktop 728x90"
                  />
                ),
              }}
              adsense={
                <Ad
                  key={id}
                  className="adsbygoogle"
                  style={{
                    display: "block",
                    margin: "auto",
                    width: 728,
                    height: 90,
                  }}
                  data-ad-client="ca-pub-3154552656014103"
                  data-ad-slot="9551701632"
                />
              }
            />
          </div>}
          <div className="listings-from-seller">
            {/*
              A seller with fewer than SELLER_ROW_MIN other listings made a thin
              row, or none at all -- which left the ad above and the ad below
              stacked with nothing between them. That seller's slot goes to a
              row picked by the buyer's own interests instead.
            */}
            <Showcase
              minShown={SELLER_ROW_MIN}
              fallback={
                <AffinityShowcase
                  listingId={Number(id)}
                  listingTags={product?.tags}
                  setProduct={setProduct as unknown as (product: Record<string, unknown>) => void}
                  tag={tag}
                  onEmpty={markBelowEmpty}
                />
              }
              title={`${otherLabel} ${userName}`}
              searchParams={{
                userId: user.id,
                hasPhoto: true,
              }}
              listName="otherList"
              setProduct={setProduct as (product: unknown) => void}
              tag={tag}
              itemsPerPage={10}
              excludeListingId={Number(id)}
              lazy={true}
              noMargin={false}
              recommendForever
            />
          </div>
          {!IS_MOBILE && emptyBelowFor !== Number(id) && <div className="from-seller-ad">
            <AdFramework
              bidstream={{
                mobile: (
                  <div
                    data-ad-tagid="wgw8vFZoalasLTuQSL4Q"
                    data-zid="1018"
                    data-name="SW - sellwild.com mobile 300x250"
                  />
                ),
                desktop: (
                  <div
                    data-ad-tagid="Yp4BKeY0meZZk0eOYBue"
                    data-zid="1014"
                    data-name="SW - sellwild.com desktop 728x90"
                  />
                ),
              }}
              adsense={
                <Ad
                  key={id}
                  className="adsbygoogle"
                  style={{
                    display: "block",
                    margin: "auto",
                    width: 728,
                    height: 90,
                  }}
                  data-ad-client="ca-pub-3154552656014103"
                  data-ad-slot="9551701632"
                />
              }
            />
          </div>}
        </section>
      </div>
    </>
  );
};

export default ProductDetail;
